import Vue from "vue";
import { SchoolModule } from "./models/SchoolModule";
import { BackEndService } from 'src/utils/BackEndService';
import { SchoolModuleStep, SchoolModuleStepExercise, SchoolModuleStepType } from "./models/SchoolModuleSteps";
import { getDiscreteTempo } from "../global/types/DiscreteTempo";
import { playablePool } from "../playable/utils/PlayablePool";
import { analyticsController } from "../user/analytics/AnalyticsController";
import { SchoolModuleSequence } from "./models/SchoolModuleSequence";
import { onboardingChecklistController } from "../onboarding/checklist/OnboardingChecklistController";
import router from "../../router";
import { userService } from "../user/UserService";
import { playerConfig } from "../player/models/PlayerConfig";
import type { Division } from "../playable/models/Division";
import { type Exercise } from "./models/Exercise";
import { VisualParameters } from "../player/models/parameters/VisualParameters";

interface SchoolServiceState {
  modules: any[],
  sequences: SchoolModuleSequence[],
  activeSequence: SchoolModuleSequence | null,
  activeModule: SchoolModule | null,
  activeStep: SchoolModuleStep | null,
  recommendedModules: {
    started: SchoolModuleStep[],
    notStarted: SchoolModuleStep[]
  }
}

class SchoolService {
  public readonly state = Vue.observable<SchoolServiceState>({
    modules: [],
    sequences: [],
    activeSequence: null,
    activeModule: null,
    activeStep: null,
    recommendedModules: {
      notStarted: [],
      started: []
    }
  })


  public loadAllModules() {
    return BackEndService.sendGetRequest('school-modules').then((response: any) => {
      this.state.modules = response.content.modules;
      this.state.sequences = response.content.sequences.map((sequence: any) => new SchoolModuleSequence(sequence));
      this.state.activeSequence = this.state.sequences.find(seq => seq.level === userService.user.level.value) || null

      const started = this.state.activeSequence?.content.filter(module => module.school_module_progress && !module.school_module_progress.is_finished).slice(0, 2) || []
      const notStarted = this.filterNotStarted()
      this.state.recommendedModules = { started, notStarted }
    })
  }

  private currentLoadModulePromise: Promise<any> | null = null
  public loadModule(id: number, force: boolean = false) {
    if ((this.state.activeModule?.id !== id && !this.currentLoadModulePromise) || force) {
      this.currentLoadModulePromise = BackEndService.sendGetRequest(`school-module/${id}`).then((response: any) => {
        this.state.activeModule = new SchoolModule(response.content);
      })
        .finally(() => {
          this.currentLoadModulePromise = null
        })

      return this.currentLoadModulePromise
    }

    if (this.currentLoadModulePromise) {
      return this.currentLoadModulePromise
    }

    return Promise.resolve();
  }
  public resetCurrentModule() {
    this.state.activeModule = null;
  }

  async loadStepForSection(exerciseId: number) {
    const response = await BackEndService.sendGetRequest(`exercises/${exerciseId}/sheet`)
    const activeStepExercise = new SchoolModuleStepExercise(response.content.model, null, null)
    const currentSection = playablePool.get('section', activeStepExercise.sectionId)!;
    this.state.activeStep = activeStepExercise;
    console.log('loadStepForSection')
    playerConfig.setSpeed(getDiscreteTempo(currentSection.temporalIndicators.tempo, activeStepExercise.startTempo))
    playerConfig.setLoop('PLAY', currentSection)
    playerConfig.setVolumeActive('METRONOME', true)
    playerConfig.setVisualParameters(new VisualParameters({
      LEFT: response.content.model.hand === 'LEFT' || response.content.model.hand === 'BOTH',
      RIGHT: response.content.model.hand === 'RIGHT' || response.content.model.hand === 'BOTH',
      LYRICS: true
    }))
    const divisions = Object.values(response.content.divisions)[0] as Division[]
    const exercise: Exercise = response.content.model

    return { section: currentSection, divisions, hasMusicxml: !!activeStepExercise.musicxml, exercise }
  }

  validateCurrentStep(difficultyFelt: number | null = null) {
    return this.validateStep(this.state.activeStep!.index, this.state.activeStep!.type, false, difficultyFelt)
  }
  async validateStep(stepIndex: number, stepType: SchoolModuleStepType, skipped: boolean = false, difficultyFelt: number | null = null) {
    await BackEndService.sendPostRequest(`school-module/${this.state.activeModule!.id}/progress`, {
      'max_step_index': this.state.activeStep!.index,
      'exercise_id': stepType === 'EXERCISE' ? (this.state.activeStep as SchoolModuleStepExercise).exerciseId : null,
      'difficulty_felt': difficultyFelt
    })

    await this.loadModule(this.state.activeModule!.id, true)

    /* if (stepType === 'EXERCISE' && router.currentRoute.name !== 'Player Evaluation') {
      onboardingChecklistController.completeTask('tokatab_exercises')
    } */

    analyticsController.logBasicAnalyticsEvent(skipped ? 'skipped premium' : 'completed' + ' school step', 'School', {
      moduleId: this.state.activeModule!.id,
      stepIndex: stepIndex,
      stepType: stepType,
    })

    if (this.state.activeModule!.progress.isFinished) {
      analyticsController.logBasicAnalyticsEvent('completed module', 'School', {
        moduleId: this.state.activeModule!.id
      })
      if (router.currentRoute.name !== 'Player Evaluation') {
        onboardingChecklistController.completeTask('first_module')
      }
    }
  }

  getNextStepAction(exerciseId?: number): 'LOAD_NEXT_EXERCISE' | 'BACK_TO_MODULE_PAGE' {
    const currentIndex = schoolService.state.activeStep?.index || schoolService.state.activeModule!.content.findIndex(el => el.type === 'EXERCISE' && (el as SchoolModuleStepExercise).exerciseId === exerciseId);
    const nextStep = this.state.activeModule!.content[currentIndex + 1]

    if (nextStep && nextStep instanceof SchoolModuleStepExercise) {
      return 'LOAD_NEXT_EXERCISE'
    }
    else {
      return 'BACK_TO_MODULE_PAGE'
    }
  }

  private filterNotStarted() {
    const isNotCompleted = module => !module.school_module_progress;
    const isFreeModule = module => module.is_free && !module.school_module_progress;

    if (!this.state.activeSequence) {
      return []
    }

    if (userService.user.premium) {
      // Premium users get the first 2 uncompleted modules
      return this.state.activeSequence.content.filter(isNotCompleted).slice(0, 2);
    }
    else {
      // Non-premium users get free modules, or if none exist,
      // the first 2 uncompleted modules
      const freeModules = this.state.activeSequence.content.filter(isFreeModule);

      return freeModules.length > 0
        ? freeModules
        : this.state.activeSequence.content.filter(isNotCompleted).slice(0, 2);
    }
  }


}

export const schoolService = new SchoolService();
